import { HttpMethods, makeRequest } from '../helpers/http.helper';
import {
  OrganizationOnboardingFormState,
  requiresMwanFields,
} from '../types/organization-onboarding.type';
import { OrganizationType } from '../types/organization.type';
import { WASTE_TYPE_OTHER } from '../constants/wasteTypes';
import {
  GenericResponse,
  OrganizationOnboardingSubmitResponse,
} from '../types/form-submission.type';

function unwrapOnboardingSubmitResult(body: unknown): OrganizationOnboardingSubmitResponse {
  const data = body as GenericResponse<OrganizationOnboardingSubmitResponse>;
  const r = data?.result;
  if (
    !r ||
    typeof r !== 'object' ||
    typeof (r as { submission_id?: unknown }).submission_id !== 'string' ||
    !(r as { submission_id: string }).submission_id.trim()
  ) {
    throw new Error('Invalid response from server: missing submission reference.');
  }
  return r;
}

/** POST multipart onboarding payload. Backend path: /organization/onboarding */
export function buildOrganizationOnboardingFormData(
  form: OrganizationOnboardingFormState
): FormData {
  const fd = new FormData();
  const type = form.organization_type as OrganizationType;

  fd.append('name', form.name.trim());
  fd.append('organization_type', type);
  fd.append('city', form.city.trim());
  fd.append('phone', form.phone.trim().replace(/\s/g, ''));
  if (form.contact_email.trim()) fd.append('contact_email', form.contact_email.trim());

  fd.append('organization_address', form.organization_address.trim());
  fd.append('national_address_short', form.national_address_short.trim());
  if (form.national_address_document) fd.append('national_address_document', form.national_address_document);
  fd.append('cr_number', form.cr_number.trim());
  if (form.cr_document) fd.append('cr_document', form.cr_document);
  fd.append('vat_number', form.vat_number.trim());
  if (form.vat_document) fd.append('vat_document', form.vat_document);
  if (form.iban_certificate_document) fd.append('iban_certificate_document', form.iban_certificate_document);
  fd.append('iban', form.iban.trim());
  fd.append('bank_name', form.bank_name.trim());
  fd.append('bank_account_number', form.bank_account_number.trim());

  if (requiresMwanFields(type)) {
    if (form.mwan_certificate_document) fd.append('mwan_certificate_document', form.mwan_certificate_document);
    fd.append('mwan_license_number', form.mwan_license_number.trim());
  }

  fd.append('waste_types', JSON.stringify(form.waste_types));
  fd.append('waste_type_names', JSON.stringify(form.waste_type_names));
  if (form.waste_types.includes(WASTE_TYPE_OTHER)) {
    fd.append('other_waste_type', form.other_waste_type.trim());
  }
  fd.append('google_maps_url', form.google_maps_url.trim());
  if (form.notes.trim()) fd.append('notes', form.notes.trim());

  fd.append('is_24_7', String(form.is_24_7 === true));
  if (form.is_24_7 === false) {
    fd.append('operation_days', JSON.stringify(form.operation_days));
    if (form.operation_start) fd.append('operation_start', form.operation_start.toISOString());
    if (form.operation_end) fd.append('operation_end', form.operation_end.toISOString());
  }

  fd.append('policies_acknowledged', 'true');

  return fd;
}

/** Returns parsed `result` from GenericResponse (`submission_id`, `status`). */
export async function submitOrganizationOnboarding(
  form: OrganizationOnboardingFormState
): Promise<OrganizationOnboardingSubmitResponse> {
  const data = buildOrganizationOnboardingFormData(form);
  const body = await makeRequest({
    url: '/organization/onboarding',
    method: HttpMethods.POST,
    data,
  });
  return unwrapOnboardingSubmitResult(body);
}
